<script setup lang="ts">
/**
 * Feature popup shown once after a release (RDT-286): the new customer-facing features this trainer
 * has not been told about yet, capped by the backend, with a link to the full release-notes page.
 *
 * Deliberately quiet:
 * - nothing to show ⇒ no dialog at all;
 * - a trainer with no marker yet (brand new, or the first time this ships) is silently marked as
 *   up to date, so nobody is greeted with months of history — the *next* release is their first popup;
 * - suppressed while the new-account flow owns the screen (that already asks them to fill in settings).
 */
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { ProfileAdministrationAPI } from "../../api";
import { isNewAccount } from "../../utils/GeneralUtils.ts";
import { i18n } from "../../main.ts";
import { CUSTOMER_RELEASE_NOTES_FLAG, useFeatureFlag } from "../../composables/useFeatureFlag.ts";
import type { ReleaseNote, Trainer } from "../../types/PersologOnlineTypes.ts";

const props = defineProps<{ trainer: Trainer | null | undefined }>();

const { t } = useI18n();
const router = useRouter();

// While the feature is off for this environment nothing happens at all — no request, no marker, so
// a trainer's "last seen" is not quietly advanced past releases they were never shown.
const enabled = useFeatureFlag(CUSTOMER_RELEASE_NOTES_FLAG);

const visible = ref(false);
const notes = ref<ReleaseNote[]>([]);

function noteText(note: ReleaseNote): string {
    return i18n.global.locale.value === "en" ? note.noteEn || note.noteDe : note.noteDe;
}

/** Remember that these features have been shown, so the popup does not come back. */
async function markSeen(trainerId: string) {
    try {
        await ProfileAdministrationAPI.markReleaseNotesSeen(trainerId);
    } catch (error) {
        // Never block the app on this: worst case the popup appears once more.
        console.error("Failed to record release notes as seen:", error);
    }
}

async function close() {
    visible.value = false;
    if (props.trainer) await markSeen(props.trainer.id);
}

async function showAll() {
    await close();
    await router.push("/release-notes");
}

// Watched rather than run on mount: the Unleash client resolves asynchronously, so the flag can
// still be false when this component mounts and only turn true a moment later. Vue's `once` would be
// consumed by the immediate run, so a plain guard keeps it to one check per page load.
let checked = false;

watch(enabled, async (isEnabled) => {
    const trainer = props.trainer;
    if (checked || !isEnabled || !trainer || isNewAccount()) return;
    checked = true;

    // Not tracked yet: start tracking silently rather than replaying past releases.
    if (!trainer.lastSeenReleaseNotesAt) {
        await markSeen(trainer.id);
        return;
    }

    try {
        notes.value = await ProfileAdministrationAPI.fetchNewReleaseNotes(trainer.id);
        visible.value = notes.value.length > 0;
    } catch (error) {
        console.error("Failed to load new release notes:", error);
    }
}, { immediate: true });
</script>

<template>
    <Dialog
        v-model:visible="visible"
        modal
        dismissable-mask
        :header="t('releaseNotes.popup.heading')"
        :style="{ width: '32rem', maxWidth: '92vw' }"
        data-cy="release-notes-popup"
        @hide="close"
    >
        <p class="mt-0">{{ t("releaseNotes.popup.intro") }}</p>
        <ul class="note-list" data-cy="release-notes-popup_notes">
            <li v-for="note in notes" :key="note.issueKey" class="mb-2">{{ noteText(note) }}</li>
        </ul>

        <template #footer>
            <Button
                :label="t('releaseNotes.popup.showAll')"
                icon="pi pi-external-link"
                text
                data-cy="release-notes-popup_show-all"
                @click="showAll"
            />
            <Button
                :label="t('releaseNotes.popup.dismiss')"
                icon="pi pi-check"
                data-cy="release-notes-popup_dismiss"
                @click="close"
            />
        </template>
    </Dialog>
</template>

<style scoped>
/* Tailwind preflight strips list markers; restore bullets so each note reads as a list item. */
.note-list {
    padding-left: 1.25rem;
    list-style: disc;
    margin: 0;
}
</style>
